import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!token) setErr("Missing or invalid reset link. Request a new one.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 6) { setErr("Password must be at least 6 characters."); return; }
    if (pw !== pw2)    { setErr("Passwords don't match.");                  return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => nav("/login"), 2500);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 pt-16 pb-24" data-testid="reset-password-page">
      <div className="text-[11px] uppercase tracking-[0.22em] text-muted font-bold">Ministry of Exchange</div>
      <h1 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-ink">Set a new password</h1>

      {done ? (
        <div data-testid="reset-password-success" className="mt-8 p-5 rounded-xl border border-ink/10 bg-white">
          <div className="flex items-start gap-3">
            <CheckCircle2 size={22} className="text-elec shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-bold text-ink">Password updated.</div>
              <p className="mt-1 text-[13px] text-muted">Redirecting you to log in…</p>
            </div>
          </div>
        </div>
      ) : (
        <>
          <p className="mt-3 text-sm text-muted max-w-sm">
            Choose a password you'll remember, at least 6 characters.
          </p>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="moe-field">
              <input
                data-testid="reset-password-new"
                type="password" required autoFocus minLength={6}
                value={pw} onChange={(e) => setPw(e.target.value)}
                placeholder=" "
              />
              <label>New password</label>
            </div>
            <div className="moe-field">
              <input
                data-testid="reset-password-confirm"
                type="password" required minLength={6}
                value={pw2} onChange={(e) => setPw2(e.target.value)}
                placeholder=" "
              />
              <label>Confirm new password</label>
            </div>
            {err && <div className="text-sm text-red-600" data-testid="reset-password-error">{err}</div>}
            <button
              data-testid="reset-password-submit"
              disabled={busy || !token || !pw || !pw2}
              className="moe-btn moe-btn-primary w-full disabled:opacity-50"
            >
              {busy ? "Updating…" : <>Update password <ArrowUpRight size={15} /></>}
            </button>
          </form>
        </>
      )}

      <div className="mt-8 text-sm text-muted">
        <Link to="/login" className="text-elec font-semibold">Back to log in →</Link>
      </div>
    </div>
  );
}
